import PubSub from 'pubsub-js';
import {
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction
} from 'mobx';
import debounce from 'lodash/debounce';

import PubSubObserver from 'lib/PubSubObserver';
import { AppNotificationSound } from 'lib/AppNotificationSound';
import { apiUrls, urls } from 'shared/entities/domain';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { ComponentType } from 'shared/entities/components/Component';
import { IRootStore } from 'shared/entities/store/rootStore';
import {
  IManagerNotificationsStore,
  StateRefreshMode
} from 'shared/entities/store/managerNotificationsStore';
import {
  ManagerNotificationEventData,
  ManagerNotificationsEventServer,
  ManagerNotificationOpenViewCheck,
  ITabIndicator,
  ManagerNotificationsStateServer,
  ManagerNotificationsSubscriptionResponseServer,
  ManagerNotificationType,
  TabIndicatorLevel,
  TabIndicatorSource,
  MarkNoteMentionsReadResponseServer,
  PubSubManagerNotificationEvent,
  PubSubNoteMentionsReadData,
  isSlaToastEvent,
  SlaToastNotificationType,
  UserListFillEventType
} from 'shared/entities/managerNotifications';
import { WsEvent } from 'shared/entities/ws/server';
import { WsConnectionState } from 'shared/entities/ws';
import { ListEntityType } from 'shared/entities/lists';
import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { SlaToastBurstAction, SlaToastBurstGateModel } from 'shared/models/sla';
import { setFaviconIndicator } from 'shared/utils/tabIndicator';
import { ButtonSize } from 'shared/newEntities/components/Button';

const STATE_REFRESH_DELAY = 200;
const EXTENDED_STATE_REFRESH_DELAY = 800;
/**
 * Дедуп нужен только на окно повторной доставки после реконнекта, а не на всю
 * сессию — держим последние N, иначе набор растёт, пока открыта вкладка.
 */
const PROCESSED_EVENT_IDS_LIMIT = 500;

export class ManagerNotificationsStore
  extends PubSubObserver
  implements IManagerNotificationsStore
{
  readonly hasUnreadConversations: FieldModel<boolean> =
    new FieldModel<boolean>(false);
  readonly unreadConversationsCount: FieldModel<number> =
    new FieldModel<number>(0);
  readonly hasNoteMentions: FieldModel<boolean> = new FieldModel<boolean>(
    false
  );
  readonly noteMentionsCount: FieldModel<number> = new FieldModel<number>(0);

  private readonly rootStore: IRootStore;
  private readonly obtainTokenStage: LoadingStageModel =
    new LoadingStageModel();
  private _channelKey: string | null = null;
  /** Непрочитанные диалоги, известные поимённо: из get_state и из событий */
  private unreadConversationIds: Set<number> = new Set();
  /**
   * Сколько непрочитанных не попало в список из-за обрезания на бэке. Их id мы
   * не знаем, поэтому снять их поимённо нельзя — только следующей сверкой.
   */
  private _untrackedUnreadConversationsCount = 0;
  /**
   * SLA-срез лежит рядом с unread/mention, но отдельно от них: это не второй
   * стор и не дубль коллекции непрочитанных. Счётчики приходят уже
   * отфильтрованными под роль — считать релевантность на фронте не нужно.
   */
  private slaWarningConversationIds: Set<number> = new Set();
  private _slaWarningsCount = 0;
  private slaViolationConversationIds: Set<number> = new Set();
  private _slaViolationsCount = 0;
  /** Centrifugo может доставить событие повторно после reconnect */
  private readonly processedEventIds: Set<string> = new Set();
  /** Время расчёта последнего get_state — чтобы не посчитать одно изменение дважды */
  private lastStateUpdatedAt: number | null = null;
  private wasEverConnected = false;
  private focusListenersAttached = false;
  /** Актуальный GET get_state: предыдущий отменяется при refresh/смене проекта. */
  private _abortController: AbortController | null = null;
  private readonly sound: AppNotificationSound = new AppNotificationSound();
  private readonly slaToastBurstGate: SlaToastBurstGateModel;
  private readonly openViewCheckers: Map<
    ManagerNotificationType,
    Set<ManagerNotificationOpenViewCheck>
  > = new Map();

  constructor(rootStore: IRootStore) {
    super();

    this.rootStore = rootStore;
    this.slaToastBurstGate = new SlaToastBurstGateModel(
      this.handleSlaToastBurstAction
    );

    /*
     * Остальное состояние стора живёт в FieldModel и наблюдаемо само по себе,
     * а SLA-срез — обычные поля, поэтому его надо объявить явно. Иначе реакция
     * favicon и заголовок вкладки не увидят изменения, пришедшие только по SLA:
     * счётчики поменяются, а перерисовки не будет до следующей перезагрузки.
     *
     * Наблюдаемы именно счётчики: множества id мутируются add/delete, и
     * количество всегда пересчитывается из их размера.
     */
    makeObservable<
      ManagerNotificationsStore,
      '_slaWarningsCount' | '_slaViolationsCount'
    >(this, {
      _slaWarningsCount: observable,
      _slaViolationsCount: observable,

      tabIndicator: computed,
      slaViolationsCount: computed,
      slaWarningsCount: computed
    });
  }

  /**
   * Канал приносит только новые события, про накопленное до входа их не будет —
   * текущее значение счётчиков даёт get_state.
   *
   * Сначала подписка, потом снимок: событие между снимком и подпиской не попадёт
   * ни туда, ни сюда. Дедуп по created_at спасает от двойного счёта, не от потери.
   *
   * При ошибке connect снимок всё равно нужен: realtime не будет, но стартовые
   * индикаторы верны.
   */
  init = async (): Promise<void> => {
    await this.connect();

    void this.loadState();
  };

  /** Подписка на личный канал менеджера поверх уже поднятого проектного WS-клиента. */
  private connect = async (): Promise<void> => {
    const projectId = this.rootStore.projectId;

    if (!projectId || this.obtainTokenStage.isLoading || this._channelKey) {
      return;
    }

    this.obtainTokenStage.loading();

    const response =
      await this.rootStore.networkStore.api<ManagerNotificationsSubscriptionResponseServer>(
        apiUrls.MANAGER_NOTIFICATIONS_OBTAIN_SUBSCRIPTION_TOKEN
      );

    // за время запроса могли переключить проект — и токен, и состояние уже от чужого
    if (this.rootStore.projectId !== projectId) {
      return;
    }

    if (response.isError) {
      this.obtainTokenStage.error();
    } else {
      const { channel, token } = response.data;

      this.rootStore.websocketStore.subscribeToChannel({
        key: channel,
        listeners: [this.handleWsEvent],
        subscriptionToken: token
      });

      this._channelKey = channel;
      this.obtainTokenStage.success();
    }
  };

  get hasUnreadNotifications(): boolean {
    return this.hasUnreadConversations.value || this.hasNoteMentions.value;
  }

  /**
   * Единственный владелец агрегированного состояния индикатора. Уровень —
   * производное, а не ещё один источник истины: он всегда пересчитывается из
   * уже имеющихся unread/mention и SLA-среза.
   */
  get tabIndicator(): ITabIndicator {
    const sources: TabIndicatorSource[] = [];

    if (this._slaViolationsCount > 0) {
      sources.push(TabIndicatorSource.slaViolation);
    }

    if (this._slaWarningsCount > 0) {
      sources.push(TabIndicatorSource.slaWarning);
    }

    if (this.hasUnreadConversations.value) {
      sources.push(TabIndicatorSource.unread);
    }

    if (this.hasNoteMentions.value) {
      sources.push(TabIndicatorSource.mention);
    }

    return { level: this.resolveIndicatorLevel(sources), sources };
  }

  get slaViolationsCount(): number {
    return this._slaViolationsCount;
  }

  get slaWarningsCount(): number {
    return this._slaWarningsCount;
  }

  /** Порядок фиксирован: violation > warning > neutral > none. */
  private resolveIndicatorLevel = (
    sources: TabIndicatorSource[]
  ): TabIndicatorLevel => {
    if (sources.includes(TabIndicatorSource.slaViolation)) {
      return TabIndicatorLevel.violation;
    }

    if (sources.includes(TabIndicatorSource.slaWarning)) {
      return TabIndicatorLevel.warning;
    }

    return sources.length ? TabIndicatorLevel.neutral : TabIndicatorLevel.none;
  };

  /**
   * Регистрирует проверку «это уже открыто на экране» — см.
   * ManagerNotificationOpenViewCheck. type ограничивает её событиями одного вида.
   *
   * Снятие звать при размонтировании: иначе предикат закрытого диалога останется
   * в наборе и продолжит отвечать «открыто».
   */
  registerOpenViewCheck = (
    type: ManagerNotificationType,
    check: ManagerNotificationOpenViewCheck
  ): (() => void) => {
    const checkers = this.openViewCheckers.get(type) ?? new Set();

    checkers.add(check);
    this.openViewCheckers.set(type, checkers);

    return () => {
      checkers.delete(check);
    };
  };

  /** Сущность, о которой событие, сейчас открыта у оператора на экране. */
  private isEventViewOpen = (data: ManagerNotificationEventData): boolean => {
    const checkers = this.openViewCheckers.get(data.type);

    if (!checkers) {
      return false;
    }

    return Array.from(checkers).some((isOpen) => isOpen(data));
  };

  private playSoundFor = (data: ManagerNotificationEventData): void => {
    if (this.isEventViewOpen(data)) {
      return;
    }

    this.playSoundNotification();
  };

  /**
   * Единственная точка воспроизведения звука в приложении.
   */
  playSoundNotification = (): void => {
    if (!this.rootStore.userStore.soundNotificationsEnabled.value) {
      return;
    }

    this.sound.play();
  };

  /** Поднимается один раз на приложение из Root.tsx, как у websocketStore. */
  initializeReactions = (): void => {
    this.addReaction({
      key: 'wsReconnect',
      reaction: reaction(
        () => this.rootStore.websocketStore.state.value,
        this.handleWsStateChange,
        { fireImmediately: true }
      )
    });

    this.addReaction({
      key: 'projectId',
      reaction: reaction(() => this.rootStore.projectId, this.reset)
    });

    // fireImmediately: метка должна появиться по первому get_state,
    // не дожидаясь следующего изменения
    this.addReaction({
      key: 'favicon',
      reaction: reaction(() => this.tabIndicator.level, setFaviconIndicator, {
        fireImmediately: true
      })
    });

    this.attachFocusListeners();
  };

  /**
   * Внешние триггеры пересинка: реконнект, открытие диалога, возврат на вкладку.
   * Debounce схлопывает серию (быстрые клики по диалогам, alt-tab) в один запрос.
   */
  private readonly refreshStateDebouncedInternal = debounce((): void => {
    // Если общий refresh уже состоялся, отложенный extended refresh больше
    // не нужен: его данные покрывают тот же самый срез состояния.
    this.refreshStateExtendedDebounced.cancel();
    void this.loadState();
  }, STATE_REFRESH_DELAY);

  private readonly refreshStateExtendedDebounced = debounce((): void => {
    this.refreshStateDebouncedInternal();
  }, EXTENDED_STATE_REFRESH_DELAY);

  refreshStateDebounced = (mode: StateRefreshMode = 'normal'): void => {
    if (mode === 'extended') {
      this.refreshStateExtendedDebounced();
      return;
    }

    this.refreshStateDebouncedInternal();
  };

  private handleWsStateChange = (state: WsConnectionState): void => {
    if (state !== WsConnectionState.connected) {
      return;
    }

    // первое подключение уже загрузило состояние в init()
    if (this.wasEverConnected) {
      this.refreshStateDebounced();
    }

    this.wasEverConnected = true;
  };

  /** Оператор мог прочитать диалоги в другой вкладке этого же приложения. */
  private attachFocusListeners = (): void => {
    if (this.focusListenersAttached) {
      return;
    }

    this.focusListenersAttached = true;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
  };

  private detachFocusListeners = (): void => {
    if (!this.focusListenersAttached) {
      return;
    }

    this.focusListenersAttached = false;
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    window.removeEventListener('focus', this.handleWindowFocus);
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.refreshStateDebounced();
    }
  };

  private handleWindowFocus = (): void => {
    this.refreshStateDebounced();
  };

  private abortCurrentRequest = (): void => {
    this._abortController?.abort();
    this._abortController = null;
  };

  /** Начальное состояние индикаторов: до первого WS-события показывать уже нечего иначе. */
  loadState = async (): Promise<void> => {
    // Слушатели фокуса живут на всё приложение и не знают про роут: вернувшись
    // на вкладку из настроек кабинета или после логаута, оператор дёрнет
    // refreshStateDebounced, когда проекта уже нет
    const projectId = this.rootStore.projectId;

    if (!projectId) {
      return;
    }

    this.abortCurrentRequest();
    const controller = new AbortController();
    this._abortController = controller;

    const response =
      await this.rootStore.networkStore.api<ManagerNotificationsStateServer>(
        apiUrls.MANAGER_NOTIFICATIONS_GET_STATE,
        { config: { signal: controller.signal } }
      );

    // Ответ прошлого проекта / перекрытого refresh не должен писать в новый контекст
    if (
      this._abortController !== controller ||
      this.rootStore.projectId !== projectId
    ) {
      return;
    }

    this._abortController = null;

    if (response.isError) {
      return;
    }

    runInAction(() => {
      const {
        unread_conversations_count: unreadConversationsCount,
        unread_conversation_ids: unreadConversationIds
      } = response.data;

      this.lastStateUpdatedAt = new Date(response.data.updated_at).getTime();

      this.unreadConversationIds = new Set(unreadConversationIds);
      this._untrackedUnreadConversationsCount =
        unreadConversationsCount - unreadConversationIds.length;

      this.syncUnreadConversationsCount();

      this.hasNoteMentions.changeValue(response.data.has_note_mentions);
      this.noteMentionsCount.changeValue(response.data.note_mentions_count);

      // Снимок заменяет SLA-срез целиком: смешивать его с прежним контекстом
      // нельзя. Числа берём из counts, а не из длины списков — они обрезаны.
      this._slaWarningsCount = response.data.sla_warning_count;
      this.slaWarningConversationIds = new Set(
        response.data.sla_warning_conversation_ids
      );
      this._slaViolationsCount = response.data.sla_violation_count;
      this.slaViolationConversationIds = new Set(
        response.data.sla_violation_conversation_ids
      );
    });
  };

  private syncUnreadConversationsCount = (): void => {
    const total =
      this.unreadConversationIds.size + this._untrackedUnreadConversationsCount;

    this.unreadConversationsCount.changeValue(total);
    this.hasUnreadConversations.changeValue(total > 0);
  };

  /**
   * Тост о сроке ответа с переходом в диалог.
   *
   * Дедупликация уже сделана выше по `event_id`, поэтому повтор после
   * reconnect второй тост не создаёт. Роль считает бэкенд: раз событие
   * дошло, оно релевантно этому менеджеру.
   */
  private openSlaToast = (data: ManagerNotificationEventData): void => {
    const isViolation = data.type === ManagerNotificationType.slaViolation;
    const isWarning = data.type === ManagerNotificationType.slaWarning;
    const projectId = this.rootStore.projectId;

    if (
      (!isViolation && !isWarning) ||
      !projectId ||
      data.conversation_id === null ||
      // диалог уже открыт у оператора — тост только мешал бы
      this.isEventViewOpen(data)
    ) {
      return;
    }

    this.rootStore.appNotificationsStore.open({
      type: isViolation
        ? AppNotificationType.error
        : AppNotificationType.warning,
      title: (t) =>
        t(isViolation ? 'sla.violationToast' : 'sla.warningToast', {
          ns: 'stores'
        }),
      button: {
        type: ComponentType.link,
        to: urls.PROJECT.tabs.CONVERSATIONS.create({
          projectId,
          conversationId: String(data.conversation_id)
        }),
        size: ButtonSize.sm,
        children: (t) => t('sla.goToConversation', { ns: 'stores' })
      }
    });
  };

  private handleSlaToastBurstAction = (action: SlaToastBurstAction): void => {
    if (action.kind === 'individual') {
      this.openSlaToast(action.data);
      return;
    }

    this.openAggregatedSlaToast(action.type, action.count);
  };

  private canOpenSlaToast = (data: ManagerNotificationEventData): boolean =>
    Boolean(
      isSlaToastEvent(data) &&
      this.rootStore.projectId &&
      data.conversation_id !== null &&
      !this.isEventViewOpen(data)
    );

  private enqueueSlaToast = (data: ManagerNotificationEventData): void => {
    if (!this.canOpenSlaToast(data)) {
      return;
    }

    this.slaToastBurstGate.push(data);
  };

  private _handleUserListFillEvent = (
    data: Extract<
      ManagerNotificationsEventServer,
      { kind: WsEvent.userListFill }
    >['data']
  ): void => {
    if (data.project_id !== this.rootStore.projectId) {
      return;
    }

    const isFinished = data.type === UserListFillEventType.finished;

    this.rootStore.appNotificationsStore.open({
      type: isFinished
        ? AppNotificationType.success
        : AppNotificationType.error,
      title: (t) =>
        t(
          isFinished
            ? 'userListFill.notifications.fillFinished'
            : 'userListFill.notifications.fillFailed',
          {
            ns: 'usersPage',
            listName: data.list_name,
            count: data.added_count
          }
        ),
      highlightedParts: [data.list_name]
    });

    PubSub.publish(PubSubManagerNotificationEvent.userListFillReceived, data);
    void this.rootStore.listsStore.refreshListEntity({
      lType: ListEntityType.users,
      listId: data.list_id
    });
  };

  private openAggregatedSlaToast = (
    type: SlaToastNotificationType,
    count: number
  ): void => {
    const projectId = this.rootStore.projectId;

    if (!projectId) {
      return;
    }

    const isViolation = type === ManagerNotificationType.slaViolation;

    this.rootStore.appNotificationsStore.open({
      type: isViolation
        ? AppNotificationType.error
        : AppNotificationType.warning,
      title: (t) =>
        t(
          isViolation ? 'sla.violationToastGrouped' : 'sla.warningToastGrouped',
          {
            ns: 'stores',
            count
          }
        ),
      button: {
        type: ComponentType.link,
        to: urls.PROJECT.tabs.CONVERSATIONS.create({ projectId }),
        size: ButtonSize.sm,
        children: (t) => t('sla.goToConversations', { ns: 'stores' })
      }
    });
  };

  /**
   * Возвращает true, если событие про SLA и уже обработано.
   *
   * Счётчик наращивается по id диалога: бэк шлёт событие на каждый переход, а
   * повторный add того же диалога ничего не меняет. Точное число придёт
   * следующим get_state.
   */
  private applySlaEvent = (
    data: ManagerNotificationEventData,
    isCounted: boolean
  ): boolean => {
    const ids =
      data.type === ManagerNotificationType.slaViolation
        ? this.slaViolationConversationIds
        : data.type === ManagerNotificationType.slaWarning
          ? this.slaWarningConversationIds
          : null;

    if (!ids) {
      return false;
    }

    const conversationId = data.conversation_id;

    if (conversationId === null) {
      return true;
    }

    if (data.type === ManagerNotificationType.slaViolation) {
      // Нарушение снимает предупреждение по тому же диалогу: состояние одно
      this.forgetSlaConversation({
        conversationId,
        ids: this.slaWarningConversationIds,
        onRemoved: () => {
          this._slaWarningsCount = Math.max(0, this._slaWarningsCount - 1);
        }
      });
      this.rememberSlaConversation({
        conversationId,
        ids: this.slaViolationConversationIds,
        isCounted,
        onAdded: () => {
          this._slaViolationsCount += 1;
        }
      });

      return true;
    }

    this.rememberSlaConversation({
      conversationId,
      ids: this.slaWarningConversationIds,
      isCounted,
      onAdded: () => {
        this._slaWarningsCount += 1;
      }
    });

    return true;
  };

  /**
   * Счётчик ведётся дельтой к снимку, а не размером множества: списки id
   * обрезаны бэком до 500, а `counts` точные. Пересчёт из размера обрушил бы
   * счётчик до длины обрезка на первом же входящем событии.
   */
  private rememberSlaConversation = ({
    conversationId,
    ids,
    isCounted,
    onAdded
  }: {
    conversationId: number;
    ids: Set<number>;
    isCounted: boolean;
    onAdded: () => void;
  }): void => {
    if (isCounted || ids.has(conversationId)) {
      return;
    }

    ids.add(conversationId);
    onAdded();
  };

  private forgetSlaConversation = ({
    conversationId,
    ids,
    onRemoved
  }: {
    conversationId: number;
    ids: Set<number>;
    onRemoved: () => void;
  }): void => {
    if (!ids.delete(conversationId)) {
      return;
    }

    onRemoved();
  };

  /**
   * Диалог перестал быть проблемным: снимок пришёл пустым или таймер снова
   * в норме. Без этого индикатор умел только подниматься, и оператор,
   * разбирающий нарушения не уводя фокус, видел бы красный favicon до
   * ближайшей сверки.
   *
   * Промах штатен: id мог не попасть в обрезанный список, тогда счётчик
   * поправит следующий `get_state`.
   */
  clearSlaConversation = (conversationId: number): void => {
    this.forgetSlaConversation({
      conversationId,
      ids: this.slaWarningConversationIds,
      onRemoved: () => {
        this._slaWarningsCount = Math.max(0, this._slaWarningsCount - 1);
      }
    });
    this.forgetSlaConversation({
      conversationId,
      ids: this.slaViolationConversationIds,
      onRemoved: () => {
        this._slaViolationsCount = Math.max(0, this._slaViolationsCount - 1);
      }
    });
  };

  hasSlaWarning = (conversationId: number): boolean =>
    this.slaWarningConversationIds.has(conversationId);

  hasSlaViolation = (conversationId: number): boolean =>
    this.slaViolationConversationIds.has(conversationId);

  clearSlaViolation = (conversationId: number): void => {
    this.forgetSlaConversation({
      conversationId,
      ids: this.slaViolationConversationIds,
      onRemoved: () => {
        this._slaViolationsCount = Math.max(0, this._slaViolationsCount - 1);
      }
    });
  };

  /**
   * Диалог прочитан. Промах — штатный исход: чаще всего диалога и не было
   * в непрочитанных, реже он попал в обрезанный хвост списка. Во втором случае
   * счётчик поправит ближайшая сверка.
   */
  forgetUnreadConversation = (conversationId: number): void => {
    if (this.unreadConversationIds.delete(conversationId)) {
      this.syncUnreadConversationsCount();
    }
  };

  private rememberEventId = (eventId: string): void => {
    this.processedEventIds.add(eventId);

    if (this.processedEventIds.size <= PROCESSED_EVENT_IDS_LIMIT) {
      return;
    }

    // Set обходит ключи в порядке вставки — первый и есть самый старый
    const oldestId = this.processedEventIds.values().next().value;

    if (oldestId !== undefined) {
      this.processedEventIds.delete(oldestId);
    }
  };

  /**
   * Счётчик упоминаний — число, поэтому его инкремент надо защищать от повтора:
   * событие старше снимка get_state уже в нём учтено. Сравниваем строго — при
   * совпадении времён лучше посчитать дважды, чем потерять.
   */
  private isCountedInLastState = (createdAtMs: number): boolean =>
    this.lastStateUpdatedAt !== null && createdAtMs < this.lastStateUpdatedAt;

  private handleWsEvent = (payload: ManagerNotificationsEventServer): void => {
    if (payload.kind === WsEvent.userListFill) {
      if (this.processedEventIds.has(payload.data.event_id)) {
        return;
      }

      this.rememberEventId(payload.data.event_id);
      this._handleUserListFillEvent(payload.data);
      return;
    }

    if (payload.kind !== WsEvent.managerNotification) {
      return;
    }

    if (this.processedEventIds.has(payload.data.event_id)) {
      return;
    }

    this.rememberEventId(payload.data.event_id);

    // Счётчик наращиваем оптимистично: точное значение придёт следующим get_state
    const createdAtMs = new Date(payload.data.created_at).getTime();
    const isCounted = this.isCountedInLastState(createdAtMs);

    // Повторное событие старше снимка get_state уже обработано, не трогаем UI
    // notification аггрегированное и не должно вызывать повторные эффекты UI.
    if (isCounted) {
      return;
    }

    const isViewOpen = this.isEventViewOpen(payload.data);

    runInAction(() => {
      if (payload.data.type === ManagerNotificationType.unreadConversation) {
        // Счётчик показывает, сколько диалогов ждут оператора, поэтому открытый
        // не считается. Храним id, а не число: бэк шлёт уведомление на каждое
        // сообщение, а повторный add того же диалога ничего не меняет.
        if (!isViewOpen && payload.data.conversation_id !== null) {
          this.unreadConversationIds.add(payload.data.conversation_id);
        }

        this.syncUnreadConversationsCount();

        return;
      }

      if (this.applySlaEvent(payload.data, isCounted)) {
        return;
      }

      if (payload.data.type !== ManagerNotificationType.noteMention) {
        return;
      }

      this.hasNoteMentions.changeValue(true);

      if (!isCounted) {
        this.noteMentionsCount.changeValue(this.noteMentionsCount.value + 1);
      }
    });

    PubSub.publish(PubSubManagerNotificationEvent.received, payload.data);
    this.playSoundFor(payload.data);
    this.enqueueSlaToast(payload.data);
  };

  /**
   * userHasUnreadMentions передаёт вызывающий: ответ бэка — агрегат по всем
   * пользователям менеджера, и остаток по конкретному userId из него не выводится.
   * Суммарное количество упоминаний в заметках всех пользователей менеджера
   */
  markNoteMentionsRead = async ({
    userId,
    noteIds,
    userHasUnreadMentions
  }: {
    userId: string;
    noteIds: string[];
    userHasUnreadMentions: boolean;
  }): Promise<BaseResponse> => {
    // пустой note_ids — ошибка валидации на бэке, до сети не доходим
    if (!noteIds.length) {
      return { isError: true };
    }

    const response =
      await this.rootStore.networkStore.api<MarkNoteMentionsReadResponseServer>(
        apiUrls.MANAGER_NOTIFICATIONS_MARK_NOTE_MENTIONS_READ,
        {
          method: 'POST',
          data: { user_id: userId, note_ids: noteIds }
        }
      );

    if (response.isError) {
      return { isError: true };
    }

    runInAction(() => {
      this.hasNoteMentions.changeValue(response.data.has_note_mentions);
      this.noteMentionsCount.changeValue(response.data.note_mentions_count);
    });

    const eventData: PubSubNoteMentionsReadData = {
      userId,
      noteIds,
      userHasUnreadMentions
    };

    PubSub.publish(PubSubManagerNotificationEvent.noteMentionsRead, eventData);

    return { isError: false };
  };

  /** Парная к initializeReactions: она же вешает слушателей фокуса. */
  disposeAll(): void {
    this.detachFocusListeners();
    this.abortCurrentRequest();
    this.refreshStateDebouncedInternal.cancel();
    this.refreshStateExtendedDebounced.cancel();
    this.slaToastBurstGate.reset();
    super.disposeAll();
  }

  reset = (): void => {
    if (this._channelKey) {
      this.rootStore.websocketStore.deleteSubscription(this._channelKey);
      this._channelKey = null;
    }

    this.obtainTokenStage.reset();
    // иначе событие нового проекта, совпавшее по id, будет молча проигнорировано
    this.processedEventIds.clear();
    this.unreadConversationIds.clear();
    this._untrackedUnreadConversationsCount = 0;
    this.lastStateUpdatedAt = null;
    this.wasEverConnected = false;
    this.abortCurrentRequest();
    this.refreshStateDebouncedInternal.cancel();
    this.refreshStateExtendedDebounced.cancel();
    this.slaToastBurstGate.reset();
    this.hasUnreadConversations.changeValue(false);
    this.unreadConversationsCount.changeValue(0);
    this.hasNoteMentions.changeValue(false);
    this.noteMentionsCount.changeValue(0);
    // SLA-срез привязан к проекту и менеджеру: старый не должен дожить
    // до нового контекста
    this.slaWarningConversationIds.clear();
    this._slaWarningsCount = 0;
    this.slaViolationConversationIds.clear();
    this._slaViolationsCount = 0;
  };
}
